import { BOARD_WAITING_REASON_LABELS } from '@nightshift/ui-types';

import { routeUrl } from './router.ts';

/**
 * Tickets index URL state.
 *
 * Project and milestone live on the top bar (and in `/p/{slug}` + `?milestone=`).
 * State, waiting reason, and text search live on this view so Waiting/Cancelled
 * rail rows can deep-link here without the board inventing a second finder.
 */
export interface TicketsFilter {
  state: string | null;
  reason: string | null;
  q: string | null;
}

export function ticketsFilterFrom(input: {
  state?: string | null;
  reason?: string | null;
  q?: string | null;
}): TicketsFilter {
  const q = typeof input.q === 'string' ? input.q.trim() : '';
  return {
    state: emptyToNull(input.state),
    reason: emptyToNull(input.reason),
    q: q.length > 0 ? q : null,
  };
}

export function ticketsFilterToRoute(filter: TicketsFilter): TicketsFilter {
  return {
    state: emptyToNull(filter.state),
    reason: emptyToNull(filter.reason),
    q: emptyToNull(filter.q),
  };
}

export function ticketsFilterActive(filter: TicketsFilter): boolean {
  return filter.state !== null || filter.reason !== null || filter.q !== null;
}

export function ticketsEmptyCopy(opts: { filterActive: boolean }): { title: string; body: string } {
  if (opts.filterActive) {
    return {
      title: 'Nothing matches these filters.',
      body: 'Waiting and Cancelled tickets are in this list when they exist. Clear the filters to see everything.',
    };
  }
  return {
    title: 'No tickets yet.',
    body: 'Create a ticket from the top bar. This list is where Waiting and Cancelled live — the board only shows those as rail populations.',
  };
}

export function waitingReasonLabel(reason: string): string {
  return BOARD_WAITING_REASON_LABELS[reason as keyof typeof BOARD_WAITING_REASON_LABELS] ?? reason;
}

export function ticketsIndexHref(input: {
  project: string | null;
  milestone?: string | null;
  phase?: string | null;
  state?: string | null;
  reason?: string | null;
  q?: string | null;
}): string {
  return routeUrl({
    view: 'tickets',
    project: input.project,
    ticket: null,
    run: null,
    budget: null,
    group: null,
    sort: null,
    q: input.q ?? null,
    drawer: null,
    outcome: null,
    density: null,
    milestone: input.milestone ?? null,
    phase: input.phase ?? null,
    state: input.state ?? null,
    reason: input.reason ?? null,
    status: null,
    harness: null,
  });
}

function emptyToNull(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
